import { NgModule } from '@angular/core';
import { RouterModule, Routes, ActivatedRouteSnapshot } from '@angular/router';
import { Apitest } from './pages/apitest/apitest';
import { Login } from './pages/login/login';
import { CrmShell } from './pages/crm/crm-shell';
import { Auth, AuthGuard, Feature } from './auth-guard';
import { HTTP } from './services/http';
import { MessageServerAccount } from './interfaces';

// Für Seiten, die Änderungen erst per Speichern-Button schreiben: beim Verlassen fragen,
// statt ungespeicherte Eingaben stillschweigend zu verwerfen.
const confirmUnsaved = (component: { canLeave?: () => boolean | Promise<boolean> }) => component?.canLeave?.() ?? true;

export const routes: Routes = [
    { path: '', component: Login },
    { 
        path: 'tickets',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/ticket-overview-page/ticket-overview-routing.module').then((m) => m.TicketOverviewRoutingModule),
    },
    { 
        path: 'ticket-detail',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/ticket-detail/ticket-detail-routing.module').then((m) => m.TicketDetailRoutingModule),
    },
    {
        path: 'category-groups',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/category-groups/category-groups-routing.module').then((m) => m.CategoryGroupsRoutingModule),
    },
    {
        path: 'settings/reply-templates',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/reply-templates/reply-templates-routing.module').then((m) => m.ReplyTemplatesRoutingModule),
    },
    {
        // Alt-Link auf die Lead-Stufen: sie leben jetzt im CRM (Reiter "Stufen").
        path: 'settings/lead-stages',
        redirectTo: 'crm/stufen',
        pathMatch: 'full',
    },
    {
        path: 'forms',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/forms/forms-overview').then((m) => m.FormsOverview),
    },
    {
        path: 'forms/:id',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/forms/form-builder').then((m) => m.FormBuilderPage),
    },
    {
        path: 'forms/:id/responses',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/forms/form-responses').then((m) => m.FormResponses),
    },
    {
        // Öffentliche Ausfüll-Seite (ohne Login) – Mitglied füllt den Bogen per Token-Link aus
        path: 'f/:token',
        loadComponent: () => import('./pages/forms/form-fill').then((m) => m.FormFill),
    },
    {
        // Dieselbe Seite, aber am *allgemeinen* Link des Bogens: ein Link für alle, aus der
        // Bögen-Übersicht kopiert. Eigener Pfad und nicht derselbe wie oben, weil dahinter etwas
        // anderes steht – dort ein Token je Empfänger (Person bekannt), hier eines je Bogen
        // (Person erst aus der eingetragenen Adresse). `shared` sagt der Seite, welcher Fall gilt.
        path: 'fb/:token',
        data: { shared: true },
        loadComponent: () => import('./pages/forms/form-fill').then((m) => m.FormFill),
    },
    {
        path: 'selfservice',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/selfservice/selfservice-admin').then((m) => m.SelfserviceAdmin),
    },
    {
        // PDF-Formular-Designer (Studio platziert Felder aufs PDF)
        path: 'selfservice/pdf/:id',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/selfservice/selfservice-pdf-designer').then((m) => m.SelfservicePdfDesigner),
    },
    {
        // Bestätigungsseite (Double-Opt-in) – muss VOR 's/:token' stehen
        path: 's/confirm/:token',
        loadComponent: () => import('./pages/selfservice/selfservice-confirm').then((m) => m.SelfserviceConfirm),
    },
    {
        // Öffentliches Self-Service-Portal (ohne Login) – für den iframe-Embed
        path: 's/:token',
        loadComponent: () => import('./pages/selfservice/selfservice-portal').then((m) => m.SelfservicePortal),
    },
    {
        // Zusatzfelder des Personenprofils: Kategorien, Unterkategorien und Felder.
        // Die Struktur ist eine Studio-Einstellung; die Werte stehen am Kontakt.
        path: 'settings/user-fields',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/user-fields/user-fields-settings')
            .then((m) => m.UserFieldsSettings),
    },
    {
        path: 'settings/studio',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/studio-settings-page/studio-settings-page').then((m) => m.StudioSettingsPage),
        resolve: {
            studio: async () => {
                const {studio} = await HTTP.get('studio'); 
                return studio
            },
            messageServerAccounts: async () => {
                const result = await HTTP.get<MessageServerAccount[]>('integrations/list');
                return result;
            },
            categories: async () => {
                 const result = await HTTP.get<{ categories: { label: string, description?: string }[] }>('ticket/categories');
                 return result.categories;
            }
        }
    },
    {
        path: 'settings/email-accounts',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/emailaccount-page/emailaccount-page').then((m) => m.EmailAccountPage),
        resolve: {
            studio: async () => {
                const {studio} = await HTTP.get('studio'); 
                return studio
            }
        }
    },
    {
        path: 'settings/locations',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/locations/locations-routing.module').then((m) => m.LocationsRoutingModule),
    },
    {
        path: 'chatbot-overview',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/chatbot-overview/chatbot-overview-routing.module').then((m) => m.ChatbotOverviewRoutingModule),
    },
    {
        path: 'chatbot-edit',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/chatbot-edit/chatbot-edit-routing.module').then((m) => m.ChatbotEditRoutingModule),
    },
    {
        // Einstellungen -> Integrationen: a tile per connected system (today Memberbox), each one
        // connected in an overlay on this page.
        path: 'settings/integrationen',
        canActivate: [Auth({ or: ['finance.write'] }), Feature('finance')],
        loadComponent: () => import('./pages/integrationen/integrationen.page').then((m) => m.IntegrationenPage),
    },
    {
        path: 'settings/integrations',
        // the integrations (studios.read/write) and the automations (`automation`) live below
        canActivate: [Auth({or: ['studios.read', 'studios.write', 'automation']})],
        loadChildren: () => import('./pages/integrations/integrations-routing.module').then((m) => m.IntegrationsRoutingModule),
    },
    {
        path: 'notifications',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/notification-center/notification-center-routing.module').then((m) => m.NotificationCenterRoutingModule),
    },
    {
        path: 'landing-page-generator',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/landing-page-config/landing-page-config-routing.module').then((m) => m.LandingPageConfigRoutingModule),
    },
    {
        path: 'landing-pages',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/landing-page-overview/landing-page-overview-routing.module').then((m) => m.LandingPageOverviewRoutingModule),
    },
    {
        // Gewinncode einloesen und Gewinnspiele auswerten - eine Seite, nicht zwei. Eigener Pfad
        // und nicht unter `landing-pages/...`: das ist die Sicht der Theke auf einen Zettel in
        // ihrer Hand, nicht die Pflege einer Landingpage.
        path: 'gewinnspiele',
        canActivate: [Auth({ or: ['marketing.manage'] })],
        loadComponent: () => import('./pages/prize-campaigns/prize-campaigns').then((m) => m.PrizeCampaigns),
    },
    {
        path: 'gewinnspiele/:id',
        canActivate: [Auth({ or: ['marketing.manage'] })],
        loadComponent: () => import('./pages/prize-campaigns/prize-campaign').then((m) => m.PrizeCampaign),
    },
    {
        path: 'geomarketing',
        canActivate: [Auth({ or: ['users.read'] })],
        loadComponent: () => import('./pages/geomarketing/geomarketing').then((m) => m.Geomarketing),
    },
    {
        // MarketingBox — Profil, Content, Monatsplan und Meta-Kampagnen.
        // Vier Seiten auf einem Profil: was einmal ueber den Betrieb
        // eingetragen ist, gilt in allen dreien.
        path: 'marketing/profil',
        canActivate: [Auth({ or: ['marketing.manage'] }), Feature('contentBox')],
        loadComponent: () => import('./pages/marketing/marketing-profil').then((m) => m.MarketingProfilPage),
    },
    {
        path: 'marketing/content',
        canActivate: [Auth({ or: ['marketing.manage'] }), Feature('contentBox')],
        loadComponent: () => import('./pages/marketing/content-generator').then((m) => m.ContentGeneratorPage),
    },
    {
        path: 'marketing/postplan',
        canActivate: [Auth({ or: ['marketing.manage'] }), Feature('contentBox')],
        loadComponent: () => import('./pages/marketing/postplan').then((m) => m.PostplanPage),
    },
    {
        path: 'marketing/werbung',
        canActivate: [Auth({ or: ['marketing.manage'] }), Feature('contentBox')],
        loadComponent: () => import('./pages/marketing/werbung').then((m) => m.WerbungPage),
    },
    {
        // Geteilte Vorlage eines anderen Studios per Code übernehmen (nur eingeloggt).
        path: 'landing-pages/import',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/landing-page-import/landing-page-import').then((m) => m.LandingPageImport),
    },
    {
        path: 'landing-pages/:id/submissions',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/landing-page-submissions/landing-page-submissions').then((m) => m.LandingPageSubmissions),
        resolve: {
            submissions: async (route: ActivatedRouteSnapshot) => {
                const landingPageId = route.paramMap.get('id');
                const result = await HTTP.get<any[]>(`landing-pages/${landingPageId}/submissions`);
                return result;
            },
            landingPageId: (route: ActivatedRouteSnapshot) => {
                return parseInt(route.paramMap.get('id') || '-1');
            },
            // Ganzes Objekt statt nur des Titels: die Leads-Übersicht braucht auch
            // configData, um zu wissen, welche Felder in den Lead-Titel gehören.
            landingPage: async (route: ActivatedRouteSnapshot) => {
                const landingPageId = route.paramMap.get('id');
                return await HTTP.get<any>(`landing-pages/${landingPageId}`);
            },
        }
    },
    {
        // Marketing -> Newsletter: bulk template sends. Gated on the Marketing permission like its menu
        // entry, and on the studio's `newsletter` feature flag -- the menu hides the entry, the guard
        // covers the deep link.
        path: 'newsletter',
        canActivate: [Auth({ or: ['marketing.manage'] }), Feature('newsletter')],
        loadChildren: () => import('./pages/newsletter/newsletter-routing.module').then((m) => m.NewsletterRoutingModule),
    },
    {
        path: 'users',
        canActivate: [AuthGuard],
        loadChildren: () => import('./pages/users/users-routing.module').then((m) => m.UsersRoutingModule),
    },
    {
        // CRM – Lead-Profil: die CRM-Sicht auf denselben Kontakt wie /users/:id (gleiche id), mit
        // Aufgaben + Verlauf. Eigene Detailroute (ohne die CRM-Tabs). VOR 'crm' halten.
        path: 'crm/lead/:id',
        canActivate: [Auth({ or: ['users.read'] })],
        loadComponent: () => import('./pages/leads/lead-profile').then((m) => m.LeadProfile),
    },
    {
        // CRM – Shell mit Top-Reitern (aktuell nur "Leads"). Der Reiter "Leads" hat eigene Unter-
        // Reiter (Liste / Pipeline) über die LeadsShell; weitere CRM-Bereiche kommen daneben.
        path: 'crm',
        component: CrmShell,
        canActivate: [Auth({ or: ['users.read'] })],
        children: [
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
            { path: 'dashboard', loadComponent: () => import('./pages/crm/crm-dashboard').then((m) => m.CrmDashboard) },
            {
                path: 'leads',
                loadComponent: () => import('./pages/crm/leads-shell').then((m) => m.LeadsShell),
                children: [
                    { path: '', redirectTo: 'liste', pathMatch: 'full' },
                    { path: 'liste', loadComponent: () => import('./pages/leads/leads-overview').then((m) => m.LeadsOverview) },
                    { path: 'pipeline', loadComponent: () => import('./pages/leads/leads-board').then((m) => m.LeadsBoard) },
                ],
            },
            {
                path: 'mitglieder',
                loadComponent: () => import('./pages/crm/members-shell').then((m) => m.MembersShell),
                children: [
                    { path: '', redirectTo: 'liste', pathMatch: 'full' },
                    { path: 'liste', loadComponent: () => import('./pages/members/members-overview').then((m) => m.MembersOverview) },
                    { path: 'board', loadComponent: () => import('./pages/members/members-board').then((m) => m.MembersBoard) },
                ],
            },
            { path: 'wiedervorlagen', loadComponent: () => import('./pages/crm/wiedervorlagen').then((m) => m.Wiedervorlagen) },
            { path: 'stufen', loadComponent: () => import('./pages/lead-stages/lead-stages-settings').then((m) => m.LeadStagesSettings) },
        ],
    },
    {
        // Team & Rollen (Mitarbeiterverwaltung) – Rechte sind App-Logik
        path: 'team',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/team/team').then((m) => m.TeamPage),
    },
    {
        // Netzwerk – geschützter Bereich, nur mit Netzwerk-Recht (oder Administration)
        path: 'network',
        canActivate: [Auth({ or: ['network.manage', 'team.manage'] })],
        loadComponent: () => import('./pages/network/network').then((m) => m.NetworkPage),
    },
    {
        // BildBox — Personenfotos, Einzelzuschnitt und Fotobestand an einem Ort
        path: 'bildbox',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/bildbox/bildbox').then((m) => m.BildboxPage),
    },
    {
        // Finance overview. Its own permission, not reports.read: salaries and private withdrawals
        // live here.
        path: 'finance',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-overview').then((m) => m.FinanceOverview),
    },
    {
        path: 'finance/entry',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-entry').then((m) => m.FinanceEntry),
    },
    {
        path: 'finance/members',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-members').then((m) => m.FinanceMembers),
    },
    {
        // Statistics by prompt (prototype): a question in plain German, counted by the server.
        path: 'finance/stats/library',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-stats-library').then((m) => m.FinanceStatsLibrary),
    },
    {
        // Statistics by prompt (prototype): a question in plain German, counted by the server.
        path: 'finance/controlling',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-controlling').then((m) => m.FinanceControlling),
    },
    {
        // Contract terms: what if everyone cancelled today, and who can cancel in a period.
        path: 'finance/laufzeiten',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-laufzeit').then((m) => m.FinanceLaufzeit),
    },
    {
        // Statistics by prompt (prototype): a question in plain German, counted by the server.
        path: 'finance/reha-auswertung',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-mitglieder').then((m) => m.FinanceMitglieder),
    },
    {
        // Statistics by prompt (prototype): a question in plain German, counted by the server.
        path: 'finance/stats',
        canActivate: [Auth({ or: ['finance.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-stats').then((m) => m.FinanceStats),
    },
    {
        path: 'finance/sharing',
        canActivate: [Auth({ and: ['finance.read', 'consultant.read'] }), Feature('finance')],
        loadComponent: () => import('./pages/finance/finance-sharing').then((m) => m.FinanceSharing),
    },
    {
        // Consultant area: the tenant is a consulting office (feature flag `consultant`) and reads the
        // finances of the studios that shared them with it.
        path: 'consultant',
        canActivate: [Auth({ or: ['consultant.read'] }), Feature('consultant')],
        loadComponent: () => import('./pages/consultant/consultant-portfolio').then((m) => m.ConsultantPortfolio),
    },
    {
        path: 'consultant/compare',
        canActivate: [Auth({ or: ['consultant.read'] }), Feature('consultant')],
        loadComponent: () => import('./pages/consultant/consultant-compare').then((m) => m.ConsultantCompare),
    },
    {
        path: 'consultant/invites',
        canActivate: [Auth({ or: ['consultant.read'] }), Feature('consultant')],
        loadComponent: () => import('./pages/consultant/consultant-invites').then((m) => m.ConsultantInvites),
    },
    {
        path: 'consultant/studio/:studioId',
        canActivate: [Auth({ or: ['consultant.read'] }), Feature('consultant')],
        data: { consultant: true },
        loadComponent: () => import('./pages/finance/finance-overview').then((m) => m.FinanceOverview),
    },
    {
        path: 'statistics',
        canActivate: [AuthGuard],
        loadComponent: () => import('./pages/statistics/statistics').then((m) => m.StatisticsPage),
    },
    {
        // Training: Übungs-/Gerätebibliothek des Studios
        path: 'training/exercises',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        loadComponent: () => import('./pages/training/exercises/exercises').then((m) => m.TrainingExercisesPage),
    },
    {
        // Training: Trainingsprogramme — eine Liste, gefiltert nach Vorlagen und Mitgliederplänen.
        // Die *Editoren* sind getrennt, weil Vorlage und Plan zwei Tabellen mit zwei
        // Endpunkt-Bäumen sind; die Liste bleibt eine, weil man sie zusammen liest.
        path: 'training/programs',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        loadComponent: () => import('./pages/training/programs/programs').then((m) => m.TrainingProgramsPage),
    },
    {
        path: 'training/templates/:id',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        canDeactivate: [confirmUnsaved],
        // `kind` entscheidet, welchen Endpunkt der Editor anspricht — er muss vor dem ersten
        // Request bekannt sein, also steht er in der Route und nicht in der Antwort.
        data: { kind: 'template' },
        loadComponent: () => import('./pages/training/program-detail/program-detail').then((m) => m.TrainingProgramDetailPage),
    },
    {
        path: 'training/plans/:id',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        canDeactivate: [confirmUnsaved],
        data: { kind: 'plan' },
        loadComponent: () => import('./pages/training/program-detail/program-detail').then((m) => m.TrainingProgramDetailPage),
    },
    {
        // Training: die Vokabulare des Studios (Zielgruppen, Kontraindikationen, Trainingsziele) —
        // früher der Reiter `training/exercises;tab=properties`.
        path: 'training/configuration',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        loadComponent: () => import('./pages/training/configuration/configuration').then((m) => m.TrainingConfigurationPage),
    },
    {
        // Training: Geräte aus dem Master-Katalog laden
        path: 'training/catalog',
        canActivate: [Auth({ or: ['train.read'] }), Feature('train')],
        loadComponent: () => import('./pages/training/exercise-catalog/exercise-catalog').then((m) => m.TrainingExerciseCatalogPage),
    },
    {
        // Master-Katalog (Geräte/Übungen/Trainingsvorlagen) – Betreiber-Recht aus Authentik, kein Studio-Recht
        path: 'catalog-admin',
        canActivate: [Auth({ or: ['catalog.manage'] }), Feature('train')],
        loadComponent: () => import('./pages/catalog-admin/catalog-admin').then((m) => m.CatalogAdminPage),
    },
    { path: 'apitest', canActivate: [AuthGuard], component: Apitest },
    // { path: '**', redirectTo: '/login' } // Wildcard route for 404 page
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule { }